/* eslint-disable i18next/no-literal-string */
import { useMemo } from "react"
import { TimeBasedComparisonProps, WeekData } from "../../../types/analytics"
import dayjs from "dayjs"

export const TimeBasedComparison: React.FC<TimeBasedComparisonProps> = ({
  analyticsItems,
  showOverlay = false,
}) => {
  const weeklyData = useMemo((): WeekData[] => {
    const weeks: WeekData[] = []
    const today = dayjs()

    for (let weekNumber = 0; weekNumber < 4; weekNumber++) {
      const weekStart = today.subtract(weekNumber * 7, "day").startOf("week")
      const weekEnd = weekStart.endOf("week")

      const weekData = analyticsItems.filter((item) => {
        const itemDate = dayjs(item.date)
        return (
          (itemDate.isAfter(weekStart) || itemDate.isSame(weekStart)) &&
          (itemDate.isBefore(weekEnd) || itemDate.isSame(weekEnd))
        )
      })

      const hasData = weekData.length > 0

      const uniqueUsers = weekData.reduce((sum, item) => sum + item.unique_users, 0)
      const vtoEvents = weekData.reduce((sum, item) => sum + item.vto_events, 0)
      const addToCartEvents = weekData.reduce((sum, item) => sum + item.add_to_cart, 0)

      let label = ""
      if (weekNumber === 0) label = "This Week"
      else if (weekNumber === 1) label = "Last Week"
      else label = `${weekNumber} Weeks Ago`

      weeks.push({
        weekNumber,
        label,
        data: {
          uniqueUsers,
          vtoEvents,
          addToCartEvents,
        },
        hasData,
      })
    }

    return weeks
  }, [analyticsItems])

  const calculateChange = (
    current: number,
    previous: number
  ): { value: number; trend: "up" | "down" | "stable" } => {
    if (previous === 0) return { value: 0, trend: "stable" }

    const change = ((current - previous) / previous) * 100
    let trend: "up" | "down" | "stable" = "stable"

    if (change > 1) trend = "up"
    else if (change < -1) trend = "down"

    return { value: Math.abs(change), trend }
  }

  const _WeekCard: React.FC<{
    week: WeekData
    previousWeek?: WeekData
    metric: "uniqueUsers" | "vtoEvents" | "addToCartEvents"
    title: string
    variant?: "primary" | "secondary"
  }> = ({ week, previousWeek, metric, title, variant = "primary" }) => {
    const currentValue = week.data[metric]
    const previousValue = previousWeek?.data[metric] || 0
    const change = calculateChange(currentValue, previousValue)
    const isPrimary = variant === "primary"

    return (
      <div
        className={`p-4 rounded-lg border ${
          isPrimary
            ? "bg-gradient-to-br from-gray-800/50 to-gray-700/30 border-gray-600/50"
            : "bg-gradient-to-br from-gray-700/50 to-gray-600/30 border-gray-500/50"
        }`}
      >
        <div className={`font-semibold ${isPrimary ? "text-gray-200" : "text-gray-300"}`}>
          {week.label}
        </div>
        <div className="text-2xl font-light mt-2 text-white">{currentValue.toLocaleString()}</div>
        <div className="text-sm text-gray-300 mt-1">{title}</div>
        {previousWeek && week.hasData && previousWeek.hasData && (
          <div className="flex items-center gap-1 mt-2">
            <div
              className={`text-sm font-medium ${
                change.trend === "up"
                  ? "text-green-400"
                  : change.trend === "down"
                  ? "text-red-400"
                  : "text-gray-300"
              }`}
            >
              {change.trend === "up" ? "↗" : change.trend === "down" ? "↘" : "→"}
              {change.value.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-400">vs {previousWeek.label.toLowerCase()}</div>
          </div>
        )}
        {!week.hasData && <div className="text-sm text-gray-500 mt-1">No data available</div>}
      </div>
    )
  }

  if (showOverlay)
    return (
      <div className="p-4 bg-dark-secondary rounded border border-gray-700">
        <h3 className="text-xl font-semibold mb-3 text-white">Weekly Comparison - Loading...</h3>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, index) => (
            <div key={index} className="p-3 bg-gray-700 rounded h-[120px]" />
          ))}
        </div>
      </div>
    )

  return (
    <div className="p-6 bg-dark-secondary rounded-lg border border-gray-700">
      <div className="flex flex-col gap-6">
        <div>
          <h3 className="text-xl analytics-section-header">📈 Week-by-Week Comparisons</h3>
          <p className="text-sm text-gray-300">
            Performance comparison across available weeks (4 weeks with data)
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="bg-gray-800 rounded-lg border border-gray-600 p-6">
            <h4 className="text-lg font-semibold mb-4 text-white">Unique Users</h4>
            <div className="space-y-4">
              {weeklyData.map((week, index) => {
                const previousWeek = weeklyData[index + 1]
                const change = previousWeek
                  ? calculateChange(week.data.uniqueUsers, previousWeek.data.uniqueUsers)
                  : { value: 0, trend: "stable" as const }

                return (
                  <div key={week.weekNumber} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-300">{week.label}</div>
                      <div className="text-lg font-light text-white">{week.data.uniqueUsers}</div>
                    </div>
                    {previousWeek && week.hasData && previousWeek.hasData && (
                      <div
                        className={`text-sm font-medium ${
                          change.trend === "up"
                            ? "analytics-trend-up"
                            : change.trend === "down"
                            ? "analytics-trend-down"
                            : "analytics-trend-stable"
                        }`}
                      >
                        {change.trend === "up" ? "↗" : change.trend === "down" ? "↘" : "→"}{" "}
                        {change.value.toFixed(0)}%
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg border border-gray-600 p-6">
            <h4 className="text-lg font-semibold mb-4 text-white">Amount of Tried On events</h4>
            <div className="space-y-4">
              {weeklyData.map((week, index) => {
                const previousWeek = weeklyData[index + 1]
                const change = previousWeek
                  ? calculateChange(week.data.vtoEvents, previousWeek.data.vtoEvents)
                  : { value: 0, trend: "stable" as const }

                return (
                  <div key={week.weekNumber} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-300">{week.label}</div>
                      <div className="text-lg font-light text-white">{week.data.vtoEvents}</div>
                    </div>
                    {previousWeek && week.hasData && previousWeek.hasData && (
                      <div
                        className={`text-sm font-medium ${
                          change.trend === "up"
                            ? "analytics-trend-up"
                            : change.trend === "down"
                            ? "analytics-trend-down"
                            : "analytics-trend-stable"
                        }`}
                      >
                        {change.trend === "up" ? "↗" : change.trend === "down" ? "↘" : "→"}{" "}
                        {change.value.toFixed(0)}%
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg border border-gray-600 p-6">
            <h4 className="text-lg font-semibold mb-4 text-white">Cart Events</h4>
            <div className="space-y-4">
              {weeklyData.map((week, index) => {
                const previousWeek = weeklyData[index + 1]
                const change = previousWeek
                  ? calculateChange(week.data.addToCartEvents, previousWeek.data.addToCartEvents)
                  : { value: 0, trend: "stable" as const }

                return (
                  <div key={week.weekNumber} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-300">{week.label}</div>
                      <div className="text-lg font-light text-white">
                        {week.data.addToCartEvents}
                      </div>
                    </div>
                    {previousWeek && week.hasData && previousWeek.hasData && (
                      <div
                        className={`text-sm font-medium ${
                          change.trend === "up"
                            ? "analytics-trend-up"
                            : change.trend === "down"
                            ? "analytics-trend-down"
                            : "analytics-trend-stable"
                        }`}
                      >
                        {change.trend === "up" ? "↗" : change.trend === "down" ? "↘" : "→"}{" "}
                        {change.value.toFixed(0)}%
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
